import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  EmptyStateLockIconComponent,
  ModalService,
  ToastService,
  toastEnum,
} from '@ui-lib';
import { TranslocoService } from '@jsverse/transloco';
import { HttpStatusCode } from '../../enums/http-status-codes.enum';
import { ApiErrorCode } from '../../enums/api-error-codes.enum';
import { AuthService } from '../../services/auth/auth.service';
import { OtpService } from '../../services/otp/otp.service';
import { ROUTE_PATHS } from '../../utils/route-paths.const';
import { SKIP_NOT_FOUND_REDIRECT } from '../../utils/http-context-tokens';
import { openErrorModal } from './utils/interceptor-modal';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const modalService = inject(ModalService);
  const toastService = inject(ToastService);
  const translocoService = inject(TranslocoService);
  const authService = inject(AuthService);
  const otpService = inject(OtpService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Errors handled globally (modals/navigation) return EMPTY to suppress them from reaching components.
      // Only unhandled errors are passed to components via throwError() for inline form validation.

      const onAuthScreen = router.url.includes(`/${ROUTE_PATHS.AUTH}`);

      // Handle API-specific error codes
      switch (error.error?.code) {
        case ApiErrorCode.BANNED_USER:
          // This can fire on a cold/refreshed request (e.g. initial profile fetch).
          return openErrorModal(translocoService, modalService, {
            testId: 'banned_user_dialog',
            titleTestId: 'banned_user_dialog_title',
            messageTestId: 'banned_user_dialog_description',
            icon: EmptyStateLockIconComponent,
            titleKey: 'auth_login_banned_dialog_title',
            messageKey: 'auth_login_banned_dialog_description',
            buttons: [
              {
                id: 'back_to_verification',
                testId: 'banned_user_dialog_cancel_button',
                label: 'common_cancel_button_label',
                variant: 'tertiary',
                btnClass: 'w-full',
                onClick: () => modalService.close(),
              },
            ],
          });

        case ApiErrorCode.ACCOUNT_DELETION_UNDER_REVIEW:
          // On the auth flow (login blocked) show the static "Unable to Log
          // In" copy, same treatment as BANNED_USER. Elsewhere (e.g. deleting
          // the account itself) show "Unable to Delete Account" with the
          // backend's own description.
          return openErrorModal(translocoService, modalService, {
            testId: 'delete_account_under_review_dialog',
            titleTestId: onAuthScreen
              ? 'delete_account_under_review_dialog_title'
              : 'delete_account_error_dialog_title',
            messageTestId: 'delete_account_under_review_dialog_message',
            icon: EmptyStateLockIconComponent,
            titleKey: onAuthScreen
              ? 'delete_account_under_review_dialog_title'
              : 'delete_account_error_dialog_title',
            messageKey: onAuthScreen
              ? 'delete_account_under_review_dialog_description'
              : undefined,
            backendMessage: error.error?.message || '',
            buttons: [
              {
                id: 'delete_account_under_review_dismiss',
                testId: 'delete_account_under_review_dialog_dismiss_button',
                label: 'general_app_okay_button_label',
                variant: 'primary',
                btnClass: 'w-full',
                onClick: () => modalService.close(),
              },
            ],
          });

        case ApiErrorCode.RATE_LIMIT_DATA_REQUEST:
          return openErrorModal(translocoService, modalService, {
            testId: 'request_data_rate_limit_dialog',
            titleTestId: 'request_data_rate_limit_dialog_title',
            messageTestId: 'request_data_rate_limit_dialog_message',
            type: 'warning',
            titleKey: 'request_data_screen_rate_limit_title',
            backendMessage: error.error?.message || '',
            buttons: [
              {
                id: 'back_to_account',
                testId: 'request_data_rate_limit_dialog_back_to_account_button',
                label: 'request_data_screen_back_to_account_button_label',
                variant: 'primary',
                btnClass: 'w-full',
                onClick: () => {
                  modalService.close();
                  router.navigate([ROUTE_PATHS.ACCOUNT], { replaceUrl: true });
                },
              },
            ],
          });

        case ApiErrorCode.NAFATH_BUSINESS_ERROR:
          return openErrorModal(translocoService, modalService, {
            testId: 'nafath_business_error_dialog',
            titleTestId: 'nafath_business_error_dialog_title',
            messageTestId: 'nafath_business_error_dialog_message',
            type: 'warning',
            titleKey: 'nafath_dialog_business_error_title',
            backendMessage: error.error?.message || '',
            buttons: [
              {
                id: 'back_to_home',
                testId: 'nafath_business_error_dialog_back_to_home_button',
                label: 'nafath_dialog_business_error_button_label',
                variant: 'primary',
                btnClass: 'w-full',
                onClick: () => {
                  modalService.close();
                  router.navigate([ROUTE_PATHS.HOME], { replaceUrl: true });
                },
              },
            ],
          });
      }

      // Handle HTTP status code errors
      switch (error.status) {
        case HttpStatusCode.UNAUTHORIZED:
          // Handle unauthorized errors (401)
          authService.clearAuthData();
          otpService.clearOtpData();

          router.navigate([ROUTE_PATHS.AUTH, ROUTE_PATHS.PHONE_VERIFICATION], {
            replaceUrl: true,
          });

          toastService.showToast({
            title: error.error?.message,
            status: toastEnum.Error,
          });

          return EMPTY;

        case HttpStatusCode.NOT_FOUND:
          // Requests that opt out (e.g. an empty sub-resource collection the
          // backend returns as 404) handle the 404 themselves — don't redirect.
          if (req.context.get(SKIP_NOT_FOUND_REDIRECT)) {
            return throwError(() => error);
          }
          // Handle not found errors (404)
          router.navigate([ROUTE_PATHS.STATUS, ROUTE_PATHS.NOT_FOUND], {
            replaceUrl: true,
          });
          return EMPTY;

        case HttpStatusCode.SERVICE_UNAVAILABLE:
          // Handle service unavailable errors (503) - Maintenance Mode
          router.navigate([ROUTE_PATHS.STATUS, ROUTE_PATHS.MAINTENANCE], {
            replaceUrl: true,
          });
          return EMPTY;

        case HttpStatusCode.NETWORK_FAILURE:
          // Handle network connection loss (status 0 indicates network failure)
          window.location.reload();
          return EMPTY;

        case HttpStatusCode.TOO_MANY_REQUESTS:
        case HttpStatusCode.INTERNAL_SERVER_ERROR:
          // These can fire on a cold/refreshed request — openErrorModal uses selectTranslate().
          return openErrorModal(translocoService, modalService, {
            testId: 'unexpected_error_dialog',
            titleTestId: 'unexpected_error_dialog_title',
            messageTestId: 'unexpected_error_dialog_description',
            type: 'error',
            titleKey: 'common_unexpected_error',
            messageKey: 'common_error_description',
            buttons: [
              {
                id: 'cancel',
                testId: 'unexpected_error_dialog_cancel_button',
                label: 'common_cancel_button_label',
                variant: 'tertiary',
                btnClass: 'w-full',
                onClick: () => modalService.close(),
              },
            ],
          });
      }

      // Fallback: surface any other backend message as a toast, unless it's
      // field-level validation errors (already shown inline by the form) or
      // we're on an auth screen (sign-in/sign-up/etc. already show their own
      // inline error banner from the same response — the 401 toast above is
      // the only one auth screens should ever see).
      if (error.error?.message && !error.error?.errors && !onAuthScreen) {
        toastService.showToast({
          title: error.error.message,
          status: toastEnum.Error,
        });
      }

      return throwError(() => error);
    })
  );
};
